import { BREAKPOINT } from '@constants/breakpoints'
import { closestIdContainingString } from '@helpers/closest'
import { useBreakpoint } from '@hooks/useBreakpoint'
import { SurfaceLevel } from '@ui-kit/types/surface'
import { FC, useEffect, useRef } from 'react'

import { Modal, ModalProps } from '../Modal'
import { SideModal } from './SideModal'

export interface SideModalProps
  extends Omit<ModalProps, 'isCentered' | 'size' | 'level'> {
  surface?: SurfaceLevel
  outsideClose?: {
    idSubstringToIgnore: string
    parentIdToStopAt?: string
  }
}

interface SideModalResponsiveWrapperProps extends ModalProps, SideModalProps {}

export const SideModalResponsiveWrapper: FC<
  SideModalResponsiveWrapperProps
> = ({
  children,
  className = '',
  onClose,
  outsideClose,
  surface = 1,

  ...restProps
}) => {
  const shouldUseDefaultModal = useBreakpoint(BREAKPOINT[720], 'max')

  const ref = useRef<HTMLDivElement>(null)

  useEffect(() => {
    function handleClickOutside(event: PointerEvent) {
      if (!outsideClose) {
        return
      }

      if (
        ref.current &&
        event.target &&
        !ref.current.contains(event.target as HTMLElement) &&
        !closestIdContainingString({
          element: event.target as HTMLElement,
          idSubstring: outsideClose?.idSubstringToIgnore,
          stopAtParentId: outsideClose?.parentIdToStopAt
        })
      ) {
        onClose()
      }
    }

    document.addEventListener('pointerdown', handleClickOutside)
    return () => document.removeEventListener('pointerdown', handleClickOutside)
  }, [ref, onClose, outsideClose])

  if (shouldUseDefaultModal) {
    return (
      <Modal className={className} onClose={onClose} {...restProps}>
        {children}
      </Modal>
    )
  }

  return (
    <SideModal
      className={className}
      onClose={onClose}
      surface={surface}
      ref={ref}
      {...restProps}
    >
      {children}
    </SideModal>
  )
}

SideModal.displayName = 'SideModal'
